import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth, useLanguage } from "@/context/AppProviders";
import { api, formatApiError } from "@/lib/api";
import { FadeIn, Overline, MaskedLine } from "@/components/Kinetic";
import { Eye, EyeOff, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function AuthPage() {
  const { t } = useLanguage();
  const { login, register, user } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = params.get("next") || "/espace-membre";

  const [mode, setMode] = useState("login"); // login | register | forgot
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) {
    navigate(next, { replace: true });
    return null;
  }

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setNotice("");
    setLoading(true);
    try {
      if (mode === "forgot") {
        await api.post("/auth/forgot-password", { email });
        setNotice(t.auth.forgotSent);
      } else {
        if (mode === "login") await login(email, password);
        else {
          await register(name, email, password);
          toast.success(t.auth.registeredNotice);
        }
        navigate(next, { replace: true });
      }
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const inputCls = "w-full bg-transparent border border-ink/20 focus:border-ember outline-none px-4 py-3.5 text-ink placeholder:text-ink/30 transition-colors duration-300 text-sm";

  const sideTitle = mode === "login" ? t.auth.loginTitle : mode === "register" ? t.auth.registerTitle : t.auth.forgotTitle;
  const sideSub = mode === "login" ? t.auth.loginSub : mode === "register" ? t.auth.registerSub : t.auth.forgotSub;

  return (
    <div className="min-h-screen grid lg:grid-cols-2" data-testid="auth-page">
      <div className="hidden lg:flex flex-col justify-between p-14 bg-ink">
        <p className="font-display font-semibold text-3xl text-white">M.SAH<span className="text-ember">.</span></p>
        <div>
          <MaskedLine innerClassName="font-display font-semibold tracking-tighter text-6xl text-white leading-[0.95]">
            {sideTitle}
          </MaskedLine>
          <FadeIn delay={0.3}>
            <p className="mt-6 text-white/50 max-w-md">{sideSub}</p>
          </FadeIn>
        </div>
        <p className="text-xs text-white/30 uppercase tracking-[0.25em]">M.SAH — 2026</p>
      </div>

      <div className="flex items-center justify-center px-6 py-32">
        <FadeIn className="w-full max-w-md">
          <Overline>{mode === "login" ? t.nav.login : mode === "register" ? t.auth.registerBtn : t.auth.forgotTitle}</Overline>
          <h1 className="lg:hidden mt-4 font-display font-semibold tracking-tighter text-4xl text-ink">{sideTitle}</h1>

          {mode === "forgot" && notice ? (
            <div className="mt-10 text-center" data-testid="forgot-success">
              <CheckCircle2 size={44} className="text-ember mx-auto" />
              <p className="mt-6 text-ink/70 text-sm leading-relaxed">{notice}</p>
              <button onClick={() => { setMode("login"); setNotice(""); }} data-testid="forgot-back-login" className="link-line mt-8 text-sm text-ink/60 hover:text-ink transition-colors">
                {t.auth.toLogin}
              </button>
            </div>
          ) : (
            <form onSubmit={submit} className="mt-10 space-y-5" data-testid="auth-form">
              {mode === "register" && (
                <div>
                  <label className="block text-xs uppercase tracking-[0.2em] text-ink/50 mb-2">{t.auth.name}</label>
                  <input data-testid="auth-name-input" value={name} onChange={(e) => setName(e.target.value)} required className={inputCls} />
                </div>
              )}
              <div>
                <label className="block text-xs uppercase tracking-[0.2em] text-ink/50 mb-2">{t.auth.email}</label>
                <input data-testid="auth-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className={inputCls} />
              </div>
              {mode !== "forgot" && (
                <div>
                  <label className="block text-xs uppercase tracking-[0.2em] text-ink/50 mb-2">{t.auth.password}</label>
                  <div className="relative">
                    <input
                      data-testid="auth-password-input"
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      minLength={6}
                      className={`${inputCls} pr-12`}
                    />
                    <button
                      type="button"
                      data-testid="password-toggle"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-ink/40 hover:text-ember transition-colors"
                      aria-label={showPassword ? t.auth.hidePassword : t.auth.showPassword}
                    >
                      {showPassword ? <EyeOff size={17} /> : <Eye size={17} />}
                    </button>
                  </div>
                  {mode === "login" && (
                    <button
                      type="button"
                      data-testid="forgot-password-link"
                      onClick={() => { setMode("forgot"); setError(""); }}
                      className="link-line mt-3 text-xs text-ink/50 hover:text-ember transition-colors"
                    >
                      {t.auth.forgotLink}
                    </button>
                  )}
                </div>
              )}

              {error && <p className="text-sm text-red-600" data-testid="auth-error">{error}</p>}

              <button
                data-testid="auth-submit-btn"
                disabled={loading}
                className="btn-glow w-full bg-ink hover:bg-ember disabled:opacity-50 text-white px-6 py-4 text-sm tracking-wide transition-colors duration-300"
              >
                {loading ? t.auth.loading : mode === "login" ? t.auth.loginBtn : mode === "register" ? t.auth.registerBtn : t.auth.forgotBtn}
              </button>
            </form>
          )}

          {!(mode === "forgot" && notice) && (
            <button
              data-testid="auth-switch-mode"
              onClick={() => { setMode(mode === "login" ? "register" : "login"); setError(""); setNotice(""); }}
              className="link-line mt-8 text-sm text-ink/60 hover:text-ink transition-colors"
            >
              {mode === "login" ? t.auth.toRegister : t.auth.toLogin}
            </button>
          )}
        </FadeIn>
      </div>
    </div>
  );
}
